import React, { useState, useMemo } from 'react';
import { FaChevronUp, FaChevronDown, FaBullseye, FaChartBar, FaWallet, FaMagic, FaArrowRight, FaClock, FaPlus, FaFire, FaPercentage, FaReceipt, FaPen, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useFinance } from '../context/FinanceContext';
import { useRecentTransactions } from '../hooks/useFirestore';
import { detectAnomalies } from '../utils/anomalyDetector';

const CHART_COLORS = ['#0EA5E9', '#10B981', '#F43F5E', '#f472b6', '#60a5fa', '#f87171', '#818cf8', '#2dd4bf'];

const Dashboard = () => {
    const { getStats, goals, targets, settings, selectedMonth, setSelectedMonth, transactions, addGoal, updateGoal, deleteGoal, addTransaction } = useFinance();
    const stats = getStats();
    const hasData = stats.income > 0 || stats.expenses > 0;
    const hasGoals = goals && goals.length > 0;
    const { data: recentTxns } = useRecentTransactions(6);
    const [showAddGoal, setShowAddGoal] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [editGoal, setEditGoal] = useState(null);

    // Anomaly detection
    const anomalies = useMemo(() => {
        if (!transactions || !settings) return [];
        return detectAnomalies(transactions, settings?.categoryBudgets || {}, stats.dailyBudget);
    }, [transactions, settings, stats.dailyBudget]);

    // Month options for last 12 months
    const monthOptions = useMemo(() => {
        const opts = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
            opts.push({ val, label });
        }
        return opts;
    }, []);

    // 6-month income vs expense data
    const barData = useMemo(() => {
        if (!recentTxns || recentTxns.length === 0) return [];
        const map = {};
        recentTxns.forEach(t => {
            const m = (t.date || '').slice(0, 7);
            if (!m) return;
            if (!map[m]) map[m] = { month: m, income: 0, expenses: 0 };
            const isCredit = t.type === 'credit' || t.type === 'income' || t.type === 'gain';
            if (isCredit) map[m].income += Math.abs(t.amount || 0);
            else map[m].expenses += Math.abs(t.amount || 0);
        });
        return Object.values(map).sort((a, b) => a.month.localeCompare(b.month)).slice(-6).map(d => ({
            ...d, label: new Date(d.month + '-01').toLocaleString('default', { month: 'short' }),
        }));
    }, [recentTxns]);

    // Category donut data
    const donutData = useMemo(() => {
        return Object.entries(stats.categoryBreakdown || {}).map(([name, value]) => ({ name, value }));
    }, [stats.categoryBreakdown]);

    // Daily heatmap data
    const heatmapData = useMemo(() => {
        const days = [];
        const [year, month] = selectedMonth.split('-').map(Number);
        const daysInMonth = new Date(year, month, 0).getDate();
        const maxSpend = Math.max(1, ...Object.values(stats.dailySpend || {}));
        for (let d = 1; d <= daysInMonth; d++) {
            const key = `${selectedMonth}-${String(d).padStart(2, '0')}`;
            const spend = stats.dailySpend?.[key] || 0;
            days.push({ day: d, spend, intensity: spend / maxSpend });
        }
        return days;
    }, [selectedMonth, stats.dailySpend]);

    // Spending velocity
    const spendVelocity = useMemo(() => {
        const cap = targets.monthlySpendCap || 1;
        return Math.min(100, (stats.expenses / cap) * 100);
    }, [stats.expenses, targets.monthlySpendCap]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Overview</p>
                    <h1 className="text-2xl font-bold tracking-tight text-frost-900">Financial Command</h1>
                    <p className="text-frost-500 mt-0.5 text-sm">Your strategic financial dashboard</p>
                </div>
                <div className="flex items-center gap-3">
                    <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
                        className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30">
                        {monthOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                    </select>
                    {anomalies.length > 0 && (
                        <div className="flex items-center gap-2 bg-gold-500/10 text-gold-400 px-3 py-2 rounded-lg border border-gold-500/20 text-xs font-semibold">
                            <FaExclamationTriangle /> {anomalies.length} Alert{anomalies.length > 1 ? 's' : ''}
                        </div>
                    )}
                    {hasData && targets.monthlySavingsGoal > 0 && (
                        <div className="flex items-center gap-2 bg-accent-500/10 text-accent-300 px-4 py-2 rounded-lg border border-accent-500/20 text-xs font-semibold">
                            <FaBullseye className="text-accent-400" />
                            {((stats.profit / targets.monthlySavingsGoal) * 100).toFixed(0)}% of Target
                        </div>
                    )}
                </div>
            </div>

            {/* SIP Banner */}
            {hasData && stats.sipSuggestion > 0 && (
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 p-5 text-white shadow-glow-lg">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M0%200h40v40H0z%22%20fill%3D%22none%22/%3E%3Cpath%20d%3D%22M20%200v40M0%2020h40%22%20stroke%3D%22rgba(255%2C255%2C255%2C0.05)%22%20stroke-width%3D%221%22/%3E%3C/svg%3E')] opacity-50"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center backdrop-blur-sm"><FaMagic className="text-lg" /></div>
                            <div>
                                <h4 className="font-semibold text-sm">SIP Opportunity Detected</h4>
                                <p className="text-xs text-white/70 mt-0.5">Surplus of ₹{stats.sipSuggestion.toFixed(0)} — deploy to investments?</p>
                            </div>
                        </div>
                        <button className="px-5 py-2 bg-white/15 backdrop-blur-sm rounded-lg text-xs font-semibold hover:bg-white/25 transition-all flex items-center gap-2 border border-white/10">
                            Deploy <FaArrowRight className="text-[10px]" />
                        </button>
                    </div>
                </div>
            )}

            {/* Cash Position Hero */}
            {stats.bankBalance > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="glass-card p-6 border-l-4 border-accent-400">
                        <p className="text-[10px] font-semibold text-frost-400 uppercase tracking-widest">Bank Balance</p>
                        <p className="text-2xl font-bold gradient-text mt-1">₹{stats.bankBalance.toLocaleString()}</p>
                        <p className="text-[10px] text-frost-400 mt-1">Across all accounts</p>
                    </div>
                    <div className="glass-card p-6 border-l-4 border-mint-400">
                        <p className="text-[10px] font-semibold text-frost-400 uppercase tracking-widest">Daily Budget Left</p>
                        <p className={`text-2xl font-bold mt-1 ${stats.dailyBudgetRemaining >= 0 ? 'text-mint-400' : 'text-gold-400'}`}>
                            ₹{Math.abs(stats.dailyBudgetRemaining).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                        <p className="text-[10px] text-frost-400 mt-1">
                            {stats.dailyBudgetRemaining >= 0 ? 'remaining today' : 'over budget today'}
                        </p>
                    </div>
                    <div className="glass-card p-6 border-l-4 border-gold-400">
                        <p className="text-[10px] font-semibold text-frost-400 uppercase tracking-widest">Cash Runway</p>
                        <p className="text-2xl font-bold text-frost-900 mt-1">
                            {stats.cashRunwayDays > 365 ? '365+' : stats.cashRunwayDays} days
                        </p>
                        <p className="text-[10px] text-frost-400 mt-1">at current spend rate</p>
                    </div>
                </div>
            )}

            {/* 6 Stats Grid */}
            {hasData ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    <StatCard label="Income" value={`₹${stats.income.toLocaleString()}`} icon={<FaChartBar />} color="from-mint-400 to-mint-600" />
                    <StatCard label="Expenses" value={`₹${stats.expenses.toLocaleString()}`} icon={<FaWallet />} color="from-gold-400 to-gold-600" />
                    <StatCard label="Savings" value={`₹${stats.profit.toLocaleString()}`} isPositive={stats.profit >= 0} icon={<FaBullseye />} color="from-accent-400 to-accent-600" />
                    <StatCard label="Daily Burn Rate" value={`₹${stats.todaySpend.toLocaleString()}`} icon={<FaFire />} color="from-orange-400 to-orange-600" />
                    <StatCard label="Savings Rate" value={`${stats.savingsRate.toFixed(1)}%`} icon={<FaPercentage />} color="from-cyan-400 to-cyan-600" />
                    <StatCard label="Transactions" value={stats.transactionCount} icon={<FaReceipt />} color="from-pink-400 to-pink-600" />
                </div>
            ) : (
                <div className="glass-card p-12 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-white/[0.03] flex items-center justify-center mb-4 border border-white/[0.04]">
                        <FaChartBar className="text-2xl text-frost-400" />
                    </div>
                    <h3 className="font-semibold text-frost-800">No financial data yet</h3>
                    <p className="text-sm text-frost-500 mt-1 max-w-sm">Upload a bank statement in Bank Intelligence to see your stats here</p>
                </div>
            )}

            {/* Charts Row */}
            {hasData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Category Donut */}
                    <div className="glass-card p-6">
                        <h3 className="text-base font-bold mb-4 text-frost-900">Category Breakdown</h3>
                        {donutData.length > 0 ? (
                            <div className="flex items-center gap-6">
                                <ResponsiveContainer width="50%" height={200}>
                                    <PieChart>
                                        <Pie data={donutData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                                            {donutData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#e2e8f0' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="flex-1 space-y-2">
                                    {donutData.map((d, i) => (
                                        <div key={d.name} className="flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2.5 h-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></div>
                                                <span className="text-frost-500">{d.name}</span>
                                            </div>
                                            <span className="font-semibold text-frost-800">₹{d.value.toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : <p className="text-sm text-frost-500 text-center py-8">No category data</p>}
                    </div>

                    {/* Income vs Expense Bar */}
                    <div className="glass-card p-6">
                        <h3 className="text-base font-bold mb-4 text-frost-900">Income vs Expenses (6 Months)</h3>
                        {barData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={barData} barGap={4}>
                                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                                    <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#e2e8f0' }} />
                                    <Bar dataKey="income" fill="#10B981" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="expenses" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <p className="text-sm text-frost-500 text-center py-8">Need more months of data</p>}
                    </div>
                </div>
            )}

            {/* Heatmap + Velocity Row */}
            {hasData && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Daily Spending Heatmap */}
                    <div className="lg:col-span-2 glass-card p-6">
                        <h3 className="text-base font-bold mb-4 text-frost-900">Daily Spending Heatmap</h3>
                        <div className="grid grid-cols-7 gap-1.5">
                            {['S','M','T','W','T','F','S'].map((d,i) => (
                                <div key={i} className="text-[10px] text-frost-400 text-center font-medium">{d}</div>
                            ))}
                            {/* Pad first week */}
                            {Array.from({ length: new Date(selectedMonth + '-01').getDay() }).map((_, i) => (
                                <div key={`pad-${i}`}></div>
                            ))}
                            {heatmapData.map(d => (
                                <div key={d.day} title={`Day ${d.day}: ₹${d.spend.toLocaleString()}`}
                                    className="aspect-square rounded-md flex items-center justify-center text-[9px] font-medium cursor-default transition-all hover:scale-110"
                                    style={{
                                        background: d.spend > 0
                                            ? `rgba(239, 68, 68, ${0.1 + d.intensity * 0.6})`
                                            : 'rgba(255,255,255,0.02)',
                                        color: d.spend > 0 ? '#e2e8f0' : '#475569',
                                    }}>
                                    {d.day}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Spending Velocity Gauge */}
                    <div className="glass-card p-6 flex flex-col items-center justify-center">
                        <h3 className="text-base font-bold mb-4 text-frost-900">Spending Velocity</h3>
                        <svg viewBox="0 0 120 80" className="w-40 h-28">
                            <path d="M10 70 A50 50 0 0 1 110 70" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="8" strokeLinecap="round" />
                            <path d="M10 70 A50 50 0 0 1 110 70" fill="none"
                                stroke={spendVelocity > 80 ? '#F43F5E' : spendVelocity > 50 ? '#0EA5E9' : '#10B981'}
                                strokeWidth="8" strokeLinecap="round"
                                strokeDasharray={`${spendVelocity * 1.57} 157`} />
                            <text x="60" y="55" textAnchor="middle" fill="#e2e8f0" fontSize="18" fontWeight="bold">{spendVelocity.toFixed(0)}%</text>
                            <text x="60" y="70" textAnchor="middle" fill="#94a3b8" fontSize="8">of spend cap</text>
                        </svg>
                        <p className="text-xs text-frost-500 mt-2">₹{stats.expenses.toLocaleString()} / ₹{targets.monthlySpendCap.toLocaleString()}</p>
                    </div>
                </div>
            )}

            {/* Quick Add Transaction */}
            <div className="flex items-center gap-3">
                <button onClick={() => setShowQuickAdd(!showQuickAdd)}
                    className="btn-pill btn-glow flex items-center gap-2 text-xs">
                    <FaPlus className="text-[10px]" /> Quick Add Transaction
                </button>
            </div>
            {showQuickAdd && <QuickAddForm onAdd={addTransaction} onClose={() => setShowQuickAdd(false)} />}

            {/* Vision Board */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold tracking-tight text-frost-900">Vision Board</h2>
                    <div className="flex items-center gap-3">
                        <span className="text-[11px] font-medium text-frost-400">{hasGoals ? `${goals.length} Goals` : 'No goals set'}</span>
                        <button onClick={() => { setEditGoal(null); setShowAddGoal(true); }}
                            className="btn-pill btn-glow flex items-center gap-2 text-xs">
                            <FaPlus className="text-[10px]" /> Add Goal
                        </button>
                    </div>
                </div>
                {hasGoals ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {goals.map(goal => (
                            <VisionCard key={goal.id} goal={goal}
                                onEdit={() => { setEditGoal(goal); setShowAddGoal(true); }}
                                onDelete={() => { if (window.confirm('Delete this goal?')) deleteGoal(goal.id); }}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="glass-card p-10 flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-xl bg-white/[0.03] flex items-center justify-center mb-4 border border-white/[0.04]">
                            <FaBullseye className="text-xl text-frost-400" />
                        </div>
                        <h3 className="font-semibold text-frost-800">No goals yet</h3>
                        <p className="text-sm text-frost-500 mt-1">Set financial goals to track your progress</p>
                    </div>
                )}
            </section>

            {/* Discipline */}
            {hasData && (
                <div className="glass-card p-6 bg-gradient-to-br from-dark-400 to-dark-600 border-accent-500/10">
                    <h2 className="text-base font-bold mb-5 text-frost-900">System Discipline</h2>
                    {targets.monthlySavingsGoal > 0 ? (
                        <div className="space-y-5">
                            <TargetItem label="Savings Target" progress={Math.min(100, (stats.profit / targets.monthlySavingsGoal) * 100)} target={`₹${(targets.monthlySavingsGoal / 1000).toFixed(0)}k`} current={`₹${(stats.profit / 1000).toFixed(0)}k`} color="accent" />
                            <TargetItem label="Spend Cap" progress={targets.monthlySpendCap > 0 ? Math.min(100, (stats.expenses / targets.monthlySpendCap) * 100) : 0} target={`₹${(targets.monthlySpendCap / 1000).toFixed(0)}k`} current={`₹${(stats.expenses / 1000).toFixed(0)}k`} color="gold" />
                        </div>
                    ) : (
                        <p className="text-sm text-frost-500 text-center py-6">Set monthly targets in Settings</p>
                    )}
                </div>
            )}

            {/* Goal Add/Edit Modal */}
            {showAddGoal && <GoalForm goal={editGoal} onSave={async (data) => {
                if (editGoal) await updateGoal(editGoal.id, data);
                else await addGoal(data);
                setShowAddGoal(false);
            }} onClose={() => setShowAddGoal(false)} />}
        </div>
    );
};

const QuickAddForm = ({ onAdd, onClose }) => {
    const [form, setForm] = useState({ description: '', amount: '', type: 'expense', category: 'Other', date: new Date().toISOString().slice(0, 10) });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.amount || !form.description) return;
        setSaving(true);
        await onAdd({ ...form, amount: Number(form.amount), date: new Date(form.date).toISOString() });
        setSaving(false);
        onClose();
    };

    return (
        <div className="glass-card p-6 space-y-4">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                <div className="md:col-span-2">
                    <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Description</label>
                    <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" placeholder="What for?" />
                </div>
                <div>
                    <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Amount</label>
                    <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" placeholder="₹" />
                </div>
                <div>
                    <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Type</label>
                    <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5 text-sm text-frost-900 focus:outline-none">
                        <option value="expense">Expense</option>
                        <option value="income">Income</option>
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Category</label>
                    <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5 text-sm text-frost-900 focus:outline-none">
                        {['Food','Shopping','Transport','Entertainment','Bills','Health','Education','Salary','Freelance','Other'].map(c =>
                            <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <button type="submit" disabled={saving} className="btn-pill btn-glow text-xs h-10">
                    {saving ? 'Saving...' : 'Add'}
                </button>
            </form>
        </div>
    );
};

const GoalForm = ({ goal, onSave, onClose }) => {
    const [form, setForm] = useState({
        title: goal?.title || '', price: goal?.price || '', saved: goal?.saved || 0,
        targetDate: goal?.targetDate || '', image: goal?.image || '',
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave({ ...form, price: Number(form.price), saved: Number(form.saved) });
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-[200] bg-dark-700/80 backdrop-blur-xl flex items-center justify-center p-6">
            <div className="bg-dark-500 w-full max-w-lg rounded-2xl p-8 shadow-card-hover border border-white/[0.06] space-y-6">
                <h3 className="text-lg font-bold text-frost-900">{goal ? 'Edit Goal' : 'Add Goal'}</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" placeholder="Goal title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <div className="grid grid-cols-2 gap-4">
                        <input type="number" placeholder="Target price" value={form.price} onChange={e => setForm({...form, price: e.target.value})}
                            className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                        <input type="number" placeholder="Saved so far" value={form.saved} onChange={e => setForm({...form, saved: e.target.value})}
                            className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    </div>
                    <input type="date" value={form.targetDate} onChange={e => setForm({...form, targetDate: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <input type="url" placeholder="Image URL (optional)" value={form.image} onChange={e => setForm({...form, image: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 py-3 border border-white/[0.06] text-frost-500 font-bold rounded-xl hover:bg-white/[0.03] transition-all text-sm">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow hover:shadow-glow-lg transition-all text-sm">
                            {saving ? 'Saving...' : goal ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const VisionCard = ({ goal, onEdit, onDelete }) => {
    const progress = goal.price > 0 ? (goal.saved / goal.price) * 100 : 0;
    const daysLeft = goal.targetDate ? Math.ceil((new Date(goal.targetDate) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

    return (
        <div className="glass-card overflow-hidden group relative">
            <div className="absolute top-3 right-3 z-20 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                <button onClick={onEdit} className="p-2 bg-white/[0.06] rounded-lg text-frost-400 hover:text-accent-400 transition-all"><FaPen className="text-[10px]" /></button>
                <button onClick={onDelete} className="p-2 bg-white/[0.06] rounded-lg text-frost-400 hover:text-gold-400 transition-all"><FaTrash className="text-[10px]" /></button>
            </div>
            {goal.image && (
                <div className="h-40 relative overflow-hidden">
                    <img src={goal.image} alt={goal.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-700 via-dark-700/40 to-transparent flex flex-col justify-end p-5">
                        <h3 className="text-lg font-bold tracking-tight text-white">{goal.title}</h3>
                        <p className="text-xs font-semibold text-accent-300 mt-0.5">₹{goal.price?.toLocaleString()}</p>
                    </div>
                </div>
            )}
            {!goal.image && (
                <div className="p-5 pb-0">
                    <h3 className="text-lg font-bold tracking-tight text-frost-900">{goal.title}</h3>
                    <p className="text-xs font-semibold text-accent-400 mt-0.5">₹{goal.price?.toLocaleString()}</p>
                </div>
            )}
            <div className="p-5 space-y-4">
                <div className="flex justify-between items-end">
                    <div>
                        <p className="text-[10px] text-frost-400 uppercase tracking-wider">Saved</p>
                        <p className="text-lg font-bold text-frost-900 mt-0.5">₹{goal.saved?.toLocaleString()}</p>
                    </div>
                    {goal.targetDate && (
                        <div className="text-right flex items-center gap-1.5 text-frost-400">
                            <FaClock className="text-[10px]" />
                            <span className="text-sm font-bold text-accent-400">{daysLeft > 0 ? daysLeft : 0}d</span>
                        </div>
                    )}
                </div>
                <div>
                    <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-accent-500 to-accent-400 rounded-full shadow-glow transition-all duration-1000" style={{ width: `${Math.min(100, progress)}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-frost-400 mt-1.5">
                        <span>{progress.toFixed(1)}%</span>
                        <span>₹{goal.price?.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, isPositive, icon, color }) => (
    <div className="glass-card p-5 stat-shine group">
        <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white text-sm shadow-lg`}>
                {icon}
            </div>
            {isPositive !== undefined && (
                <span className={`text-[10px] font-semibold flex items-center gap-1 px-2 py-0.5 rounded-md ${isPositive ? 'text-mint-400 bg-mint-500/10' : 'text-gold-400 bg-gold-500/10'}`}>
                    {isPositive ? <FaChevronUp /> : <FaChevronDown />}
                </span>
            )}
        </div>
        <p className="text-2xl font-bold tracking-tight text-frost-900">{value}</p>
        <p className="text-[11px] text-frost-500 mt-0.5">{label}</p>
    </div>
);

const TargetItem = ({ label, progress, target, current, color }) => (
    <div className="space-y-2">
        <div className="flex justify-between text-[11px]">
            <span className="text-frost-500 font-medium">{label}</span>
            <span className="text-frost-700 font-semibold">{progress.toFixed(0)}%</span>
        </div>
        <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${color === 'accent' ? 'from-accent-500 to-accent-400' : 'from-gold-500 to-gold-400'} rounded-full shadow-glow transition-all duration-1000`} style={{ width: `${Math.min(100, progress)}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-frost-400">
            <span>{current}</span>
            <span>Target: {target}</span>
        </div>
    </div>
);

export default Dashboard;
